import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setSelectedBillId } from "../../store";
import {
  useEnabledItems,
  useMenuAddons,
  useMenuModifiers,
  useLunchCatalogRefresh,
  useLunchMenuCheck,
  useMenu,
} from "../../hooks/useMenu";
import { refreshBills, useBills, useCreateBill } from "../../hooks/useBills";
import { getApiErrorMessage } from "../../utils/translations";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../hooks/useConfiguration";
import { useQueryClient } from "@tanstack/react-query";
import { useOrderingMessage } from "./components/useOrderingMessage";
import OrderingTopBar from "./components/OrderingTopBar";
import { OrderingShell } from "./components/OrderingShell";
import { useIsEdgeLocal } from "../../hooks/useEdgeMode";
import { useFirstClickFullscreen } from "../../components/profileActions";
import { OrderingSkeleton } from "./components/OrderingSkeleton";
import { CategoryChips } from "./components/menu/CategoryChips";
import { StationChips } from "./components/menu/StationChips";
import type { PreviewFilter } from "./components/menu/PreviewFilterChips";
import type { CatalogView } from "../../api/configuration";
import { ItemCatalog } from "./components/menu/ItemCatalog";
import { OrderFooter } from "./components/menu/OrderFooter";
import { ItemDetailModal } from "./components/menu/ItemDetailModal";
import {
  EditModifiersModal,
  type OrderLineChoice,
} from "./components/menu/EditModifiersModal";
import { useOrderLineEdit } from "./useOrderLineEdit";
import { useOrderingBillsRefresh } from "./useOrderingBillsRefresh";
import { DiscardOrderModal } from "./components/menu/DiscardOrderModal";
import { useTableListBills } from "./components/tables/useTableListBills";
import { TablesAutoSelect } from "./components/tables/TablesAutoSelect";
import { useBillItemsKeyboard } from "./components/tables/useOrderItemsKeyboard";
import { TablesScreen } from "./components/tables/TablesScreen";
import { TablesViewSwitch } from "./components/tables/schema/TablesViewSwitch";
import { useTablesView } from "./components/tables/schema/useTablesView";
import { useOrderingSchema } from "./components/tables/schema/useOrderingSchema";
import { isClosedEmptyBill } from "./components/tables/schema/orderingFloorData";
import { TableFooterClock } from "./components/tables/TableFooterClock";
import { NewTableFlow } from "./components/tables/manage/NewTableFlow";
import { useNewTableGuests } from "./components/tables/guests/useNewTableGuests";
import { ClientsModal } from "./components/tables/clients/ClientsModal";
import { VoiceOrderModal } from "./components/tables/voice/VoiceOrderModal";
import { useVoiceSeat } from "./components/tables/voice/useVoiceSeat";
import { useReservationConfirm } from "./components/tables/manage/useReservationConfirm";
import { useOpenNextTable } from "./components/tables/manage/useOpenNextTable";
import { OrderPanel } from "./components/menu/OrderPanel";
import { MobileOrderBar } from "./components/menu/MobileOrderBar";
import { OrderSheet } from "./components/menu/OrderSheet";
import { useKitchenHoldChoice } from "./components/menu/useKitchenHoldChoice";
import type { KitchenHoldTime } from "./components/menu/kitchenHoldItems";
import type { CreateBillIn, NewBillDiscountIn } from "../../api/bills";
import { isCloudUnreachableError } from "../../api/client";
import { isLunchItemOnSale, lunchItemId, sourceItemIdOf } from "../../lib/lunchCatalog";
import { getEndedLunchMenuIds } from "../../api/lunchMenus";
import { useEndedLunchCartItems } from "./lunch/useEndedLunchCartItems";
import { useCloseEndedLunchModals } from "./lunch/useCloseEndedLunchModals";
import { useLunchLimits } from "./lunch/useLunchLimits";
import type { MenuResponse } from "../../api/menu";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../hooks/useViewport";

type ViewMode = "tables" | "products";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment: string;
  /** Своето време за кухнята, ако е различно от това за всички (kitchenHoldItems.ts). */
  kitchenHoldTime?: KitchenHoldTime;
}

// Колко старо меню още е „свежо" при отваряне на масите / клик между масите
// (бързи последователни кликове не пращат по заявка всеки)
const LUNCH_CHECK_MAX_AGE_MS = 3000;

const Ordering = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  // Първи клик в модула → цял екран (без админския акаунт)
  useFirstClickFullscreen();
  const { isLoading: menuLoading } = useMenu();
  useLunchCatalogRefresh();
  const checkLunchMenus = useLunchMenuCheck();
  const allItems = useEnabledItems();
  const allAddons = useMenuAddons();
  const allModifiers = useMenuModifiers();
  const message = useOrderingMessage();
  // Бързото отваряне (следващ номер) пита „масата има резервация — продължаваш ли?"
  const { confirmNotice, offerFreeTable, reservationConfirmModal } = useReservationConfirm();

  const [viewMode, setViewMode] = useState<ViewMode>("tables");
  // `isFetching` НЕ се чете тук: всяко опресняване (и с еднакви данни) би
  // прерисувало целия екран два пъти — авто-изборът го следи сам (TablesAutoSelect)
  const { data: bills = [], isLoading: billsLoading } = useBills();
  const selectedBillId = useAppSelector((state) => state.bills.selectedBillId);
  // Масите като списък или върху схемата (лична настройка, само десктоп).
  const { tablesView, setTablesView, schemaMode, canSwitch } = useTablesView();
  // Масите на списъка: закритите без бон по настройката, а тези, чието
  // закриване още чака принтера — винаги и на мястото си (useTableListBills).
  // В режим „Схема" нищо не се избира само — изборът е по маса от схемата
  // (авто-изборът — `TablesAutoSelect` в JSX-а).
  const { tableBills, sortedBills, closingBillIds, showClosedTables } =
    useTableListBills({ bills });
  const selectedBill = tableBills.find((b) => b.id === selectedBillId) ?? null;
  const isSelectedBillClosing =
    selectedBillId != null && closingBillIds.has(selectedBillId);
  const createBillMutation = useCreateBill();
  const [newTableModalOpen, setNewTableModalOpen] = useState(false);
  // „Брой гости" преди „Нова маса" (Конфигурация → Маркиране)
  const {
    newTableGuests,
    askBeforeNewTable,
    guestCountModal,
    guestCountModalOpen,
  } = useNewTableGuests();
  // Гласова поръчка — микрофонът във футъра на масите
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  // „Клиенти" от футъра на продуктовия изглед — същият модал като на екрана
  // с масите; при съществуваща сметка прилага веднага, при нова маса изборът
  // изчаква в pendingDiscount и пътува с CreateBillIn при „Поръчай"
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [pendingDiscount, setPendingDiscount] =
    useState<NewBillDiscountIn | null>(null);
  const [renameTableModalOpen, setRenameTableModalOpen] = useState(false);
  const [footerModalOpen, setFooterModalOpen] = useState(false);
  const [newTableName, setNewTableName] = useState<string | null>(null);
  // „Нова маса" на друг сервитьор (изборът преди номера, `NewTableFlow`);
  // null — на влезлия. Живее и се чисти заедно с `newTableName`.
  const [newTableWaiterId, setNewTableWaiterId] = useState<number | null>(null);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState<number | null>(
    null,
  );
  // „Обобщено" в детайлите на масата — вдигнато тук, защото изборът на ред
  // (selectedOrderItemId) също е тук: обобщеният ред слива няколко записа →
  // изборът става двусмислен и се чисти при включване.
  const [billSummarized, setBillSummarized] = useState(false);
  const toggleBillSummarized = () => {
    if (!billSummarized) setSelectedOrderItemId(null);
    setBillSummarized(!billSummarized);
  };
  const [orderComment, setOrderComment] = useState("");
  const [showCommentInput, setShowCommentInput] = useState(false);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  // Режим на „Промени" за нов ред при отваряне с двоен клик от историята на маса —
  // потвърждението поръчва ДИРЕКТНО към сметката, без да минава през кошницата.
  const [reorderMode, setReorderMode] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  // Времето за кухнята в „Преглед на поръчката": пътува с „Поръчай" и се
  // нулира заедно с коментара (след поръчка и при отказ от кошницата); времето
  // на ред, различно от това за всички, живее в самите записи на кошницата.
  const kitchenHold = useKitchenHoldChoice({ orderItems, setOrderItems });
  // Обедното меню още активно ли е — при отваряне на масите, клик между масите
  // и „Добави"/нова маса (преминаване към продуктите — винаги свежо): друг
  // терминал може да го е приключил/изтрил, а часът може да е минал
  useEffect(() => {
    void checkLunchMenus(viewMode === "products" ? 0 : LUNCH_CHECK_MAX_AGE_MS);
  }, [viewMode, selectedBillId, checkLunchMenus]);
  useEndedLunchCartItems({
    allItems,
    orderItems,
    setOrderItems,
    selectedCategoryId,
    setSelectedCategoryId,
    message,
  });
  // Сигнал за ItemCatalog да зачисти търсачката 1 сек след като „Промени" за нов ред е бил затворен с "Добави".
  const [modifyAddSignal, setModifyAddSignal] = useState(0);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const closeItemModals = useCallback(() => {
    setModalOpen(false);
    setModifyModalOpen(false);
    setReorderMode(false);
  }, []);
  const lunchLimits = useLunchLimits({ allItems, orderItems, message });
  // Ред в редакция през молива в прегледа или „Промени" (F9) — EditModifiersModal
  const orderLineEdit = useOrderLineEdit({
    orderItems,
    setOrderItems,
    allowedQuantity: lunchLimits.allowedQuantity,
  });
  const editModifiersTarget = orderLineEdit.target;
  const editModifiersOpen = editModifiersTarget !== null;
  useCloseEndedLunchModals({
    allItems,
    selectedItemId,
    itemModalOpen: modalOpen || modifyModalOpen,
    closeItemModals,
    editItemId: editModifiersTarget?.itemId ?? null,
    closeEditModal: orderLineEdit.close,
    message,
  });
  // Мобилен bottom sheet „Преглед на поръчката" (отваря се от MobileOrderBar)
  const [sheetOpen, setSheetOpen] = useState(false);
  // Скрита релса с маси на тесен екран — двоен клик на маса я скрива,
  // хамбургерът в детайла я връща (демо UX; чисто UI състояние).
  const [railHidden, setRailHidden] = useState(false);
  const { isDesktop, isNarrow } = useViewport();
  // Локален режим (docs/edge.md): гласовите поръчки са извън обхвата на
  // касата — микрофонът остава, но disabled с подсказка. „Клиенти" (и
  // отстъпката при нова маса) касата сервира.
  const edgeLocal = useIsEdgeLocal();
  // Ref към актуалния submit handler — клавиатурният ефект го вика при Enter,
  // без да влачи всичките му зависимости (orderComment, кошницата и т.н.) в deps.
  const submitOrderRef = useRef<() => void>(() => {});
  // Ref към актуалния handleBack — за Esc ефекта под десктоп прага (виж долу).
  const handleBackRef = useRef<() => void>(() => {});
  // Ref към актуалния handleModify — F9 от клавиатурния ефект (преди ранния return).
  const modifyRef = useRef<() => void>(() => {});
  // Ref към актуалния handleOrderRowReorder — „+" на избран ред го вика от
  // клавиатурния hook, който седи преди ранния return (menuLoading).
  const orderRowReorderRef = useRef<
    (productId: number, lunchMenuId?: number | null) => void
  >(() => {});

  // ID-тата на продуктите, за които инкрементът отваря „Промени" за нов ред
  // (задължителни/препоръчителни добавки ИЛИ задължителен модификатор).
  const itemsRequiringAddons = useMemo(() => {
    const set = new Set<number>();
    const requiredModifierIds = new Set(
      allModifiers.filter((m) => m.isRequired).map((m) => m.id),
    );
    for (const item of allItems) {
      const has =
        item.addonIds.some((addonId) => {
          const addon = allAddons.find((a) => a.id === addonId);
          return String(addon?.type) === "2" || String(addon?.type) === "3";
        }) || item.modifierIds.some((id) => requiredModifierIds.has(id));
      if (has) set.add(item.id);
    }
    return set;
  }, [allItems, allAddons, allModifiers]);

  // Всички настройки идват с /user/info (auth.user.configuration).
  const { configuration } = useConfiguration();
  // Изглед на каталога — ЛИЧНА настройка на потребителя (org базата, следва
  // го на всяко устройство). Бутонът Плочки/Таблица във футъра (F7) я записва
  // директно (оптимистично, без localStorage) — при следващо влизане е същата.
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  const catalogView = configuration.catalogView;
  const handleCatalogViewChange = (view: CatalogView) => {
    if (view !== catalogView) updateUserConfiguration({ catalogView: view });
  };
  // Кое стои над каталога (групи/станции) — другото отива над прегледа
  // (настройка на ресторанта, Конфигурация → Маркиране)
  const catalogFilter = configuration.catalogFilter;
  // Филтър по станция — от реда над каталога (при "stations") или от реда
  // над прегледа (при "groups"); и в двата случая филтрира КАТАЛОГА
  const [selectedStationId, setSelectedStationId] = useState<number | null>(
    null,
  );
  // Редът над прегледа показва другото (групи при "stations" и обратно) и е
  // същият филтър на каталога (групите там са дърво като над каталога)
  const previewFilterKind: "station" | "group" =
    catalogFilter === "stations" ? "group" : "station";
  const previewFilter: PreviewFilter = useMemo(() => {
    if (previewFilterKind === "station") {
      return selectedStationId === null
        ? null
        : { kind: "station", id: selectedStationId };
    }
    return selectedCategoryId === null
      ? null
      : { kind: "group", id: selectedCategoryId };
  }, [previewFilterKind, selectedStationId, selectedCategoryId]);
  const handlePreviewFilterChange = (value: PreviewFilter) => {
    if (previewFilterKind === "station") {
      setSelectedStationId(value?.id ?? null);
    } else {
      setSelectedCategoryId(value?.id ?? null);
    }
  };
  // Екранна клавиатура за търсачката (F8 / бутонът във футъра), само десктоп;
  // при излизане от продуктовия изглед се скрива
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    if (viewMode !== "products") setKeyboardOpen(false);
  }, [viewMode]);
  // Бързо закриване (Конфигурация → Маркиране): „Нова маса" взима автоматично
  // най-малкия свободен номер (без модал), а след „Поръчай" се отваря „Закрий".
  const quickCloseEnabled = configuration.orderingQuickClose;
  // Номерация на масите (Конфигурация → Маркиране). „Обща номерация" ВКЛЮЧЕНА
  // (по подразбиране) = номерата са общи за ресторанта. Изключена = лична
  // номерация: всеки сервитьор си има своя и чужда маса „1" не брои за заета
  // (BE проверява по същия начин).
  const personalNumberingEnabled = !configuration.orderingSharedNumbering;
  const currentUserId = useAppSelector((state) => state.auth.userId);
  // Бързото отваряне: следващият свободен номер без модал за име (пита при
  // резервация). `handleNewTableSubmit` е по-долу — затова през затваряне.
  const openNextTable = useOpenNextTable({
    bills,
    personalNumberingEnabled,
    currentUserId,
    message,
    confirmNotice,
    offerFreeTable,
    onOpen: (tableName) => handleNewTableSubmit(tableName),
  });
  // Цялата гласова функционалност е зад правото open_bill_voice: без него
  // микрофонът не се рендира и модалът няма как да се отвори
  // (BE-то допълнително пази endpoint-а със същото право → 403).
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canVoiceOrder = permissions.some(
    (permission) => permission.name === "open_bill_voice",
  );
  // „Настани" (от „Опции" и от гласа): сметката вече е в списъка (модалът
  // изчаква презареждането) — избираме я и влизаме направо в менюто, както „Добави".
  const handleReservationSeated = (billId: number) => {
    dispatch(setSelectedBillId(billId));
    setSelectedItemId(null);
    setViewMode("products");
  };
  // Гласовото „настани резервацията на …" (voice/useVoiceSeat.tsx).
  const voiceSeat = useVoiceSeat({ onSeated: handleReservationSeated });

  // Клик по маса (списък или схема): пресни сметки + избор; null — свободна
  // маса от схемата или нищо.
  const handleTableSelect = (billId: number | null) => {
    void refreshBills(queryClient);
    dispatch(setSelectedBillId(billId));
  };
  // Двоен клик на маса със сметка в режим „Схема" — „Добави" за НЕЯ (не за
  // избраната досега); закрита без бон или чакаща принтера сметка не приема
  // поръчки (като „Добави" и F6).
  const openBillInMenu = (billId: number) => {
    const bill = tableBills.find((candidate) => candidate.id === billId);
    if (!bill || closingBillIds.has(billId) || isClosedEmptyBill(bill)) return;
    dispatch(setSelectedBillId(billId));
    setSelectedItemId(null);
    setViewMode("products");
  };
  // Режим „Схема": залата, изборът по маса и „Нова маса" за свободна маса —
  // тук, за да оцелеят при отиване в менюто и обратно.
  const schema = useOrderingSchema({
    schemaMode,
    isTablesView: viewMode === "tables",
    tableBills,
    sortedBills,
    closingBillIds,
    selectedBill,
    askBeforeNewTable,
    confirmNotice,
    message,
    onSelectBill: handleTableSelect,
    onOpenBill: openBillInMenu,
    onOpenNewTable: (tableName) => handleNewTableSubmit(tableName),
  });
  // „Клиенти" в продуктовия изглед — същото право като бутона на масите
  const canSelectCustomer = permissions.some(
    (permission) => permission.name === "select_customer",
  );
  // Активен клиентски процент по сметката (знаков: >0 отстъпка, <0 надбавка) —
  // каталогът показва коригирани цени, панелът — блока с клиента и основата
  const billDiscount =
    selectedBill?.discount_applied
      ? {
          percent: selectedBill.discount_percent ?? 0,
          clientName: selectedBill.discount_applied?.client_name ?? null,
        }
      : pendingDiscount
        ? // Нова маса: чакащият клиент се показва като приложен още преди
          // „Поръчай" (каталогът/панелът с коригирани цени)
          {
            percent: pendingDiscount.percent,
            clientName: pendingDiscount.clientName,
          }
        : null;
  // Сметка, за която да се отвори модалът „Закрий" веднага след поръчката
  // (bills списъкът първо трябва да се refetch-не — TableFooter изчаква).
  const [pendingCloseBillId, setPendingCloseBillId] = useState<number | null>(
    null,
  );
  // Свежи сметки при връщане към масите и когато менюто дойде (масите се показват)
  useOrderingBillsRefresh({ viewMode, menuLoading });

  // F7 (Плочки/Таблица) и F8 (екранна клавиатура) — само в продуктовия
  // изглед на десктоп; не зависят от избран продукт
  useEffect(() => {
    if (viewMode !== "products" || !isDesktop) return;
    const handleViewKeys = (event: KeyboardEvent) => {
      if (event.key === "F7") {
        event.preventDefault();
        updateUserConfiguration({
          catalogView: catalogView === "grid" ? "list" : "grid",
        });
      } else if (event.key === "F8") {
        event.preventDefault();
        setKeyboardOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", handleViewKeys);
    return () => window.removeEventListener("keydown", handleViewKeys);
  }, [viewMode, isDesktop, catalogView, updateUserConfiguration]);

  // Keyboard shortcuts for Info and Modify
  useEffect(() => {
    if (
      viewMode !== "products" ||
      !selectedItemId ||
      modalOpen ||
      modifyModalOpen ||
      discardModalOpen ||
      sheetOpen ||
      clientsModalOpen ||
      editModifiersOpen
    ) {
      return;
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if input/textarea is focused — освен полето за търсене на
      // каталога (настройката orderingSearchField): от него Enter подава
      // поръчката и F9 отваря „Промени", както от списъка.
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA") &&
        event.target.dataset.catalogSearch === undefined
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === "enter") {
        event.preventDefault();
        // Enter подава поръчката директно (summary екранът отпадна —
        // панелът „Преглед на поръчката" е винаги видим).
        submitOrderRef.current();
      }

      if (event.key === "F9") {
        event.preventDefault();
        modifyRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    viewMode,
    selectedItemId,
    modalOpen,
    modifyModalOpen,
    discardModalOpen,
    sheetOpen,
    clientsModalOpen,
    editModifiersOpen,
    orderItems,
    message,
    t,
  ]);

  // Esc в продуктовия изглед ПОД десктоп прага: клавиатурната машина на
  // ItemCatalog (която на десктоп хваща Esc → назад към масите) там я няма, а
  // глобалната Esc-навигация на общия терминал (SharedAccountGate) би
  // прескочила направо към „Модули"/ПИН. Затова тук връщаме стъпката
  // „продукти → маси" (с discard guard-а на handleBack) и на таблет/телефон
  // с хардуерна клавиатура. Esc никога не прескача нива.
  useEffect(() => {
    if (isDesktop || viewMode !== "products") return;
    if (
      modalOpen ||
      modifyModalOpen ||
      discardModalOpen ||
      sheetOpen ||
      clientsModalOpen ||
      editModifiersOpen
    )
      return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      )
        return;
      e.preventDefault();
      handleBackRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    isDesktop,
    viewMode,
    modalOpen,
    modifyModalOpen,
    discardModalOpen,
    sheetOpen,
    clientsModalOpen,
    editModifiersOpen,
  ]);

  // Модал/popover над екрана с масите — стрелките (маси и редове) мълчат.
  const tablesModalOpen =
    modalOpen ||
    modifyModalOpen ||
    newTableModalOpen ||
    guestCountModalOpen ||
    renameTableModalOpen ||
    footerModalOpen ||
    voiceModalOpen ||
    voiceSeat.seatModalOpen;

  // ↑ ↓ / „+" по редовете на избраната маса и → от масите към тях.
  const handleOrderItemsReorderKey = useCallback(
    (productId: number, lunchMenuId?: number | null) =>
      orderRowReorderRef.current(productId, lunchMenuId),
    [],
  );
  const billItemsKeyboard = useBillItemsKeyboard({
    bill: selectedBill,
    summarized: billSummarized,
    enabled: isDesktop && viewMode === "tables" && !tablesModalOpen,
    selectedItemId: selectedOrderItemId,
    onSelect: setSelectedOrderItemId,
    onReorder: handleOrderItemsReorderKey,
  });

  if (menuLoading) {
    // Истинският топ бар се показва веднага (не зависи от менюто) —
    // скелетонът покрива само съдържанието под него.
    return (
      <OrderingShell>
        <OrderingTopBar />
        <OrderingSkeleton label={t("ordering.loadingMenu")} />
      </OrderingShell>
    );
  }

  const selectedItem = allItems.find((item) => item.id === selectedItemId);

  // Етикет на масата за реда с категориите на продуктовия изглед (телефон)
  const tableLabel = newTableName ?? selectedBill?.name ?? "";

  const performBack = () => {
    setViewMode("tables");
    setSelectedCategoryId(null);
    setSelectedItemId(null);
    setOrderItems([]);
    setNewTableName(null);
    setNewTableWaiterId(null);
    setPendingDiscount(null);
    setOrderComment("");
    setShowCommentInput(false);
    kitchenHold.reset();
    setSheetOpen(false);
    // Списъкът сам избира първата маса; в режим „Схема" изборът остава на
    // масата/сметката, от която се е влязло в менюто.
    if (!schemaMode) dispatch(setSelectedBillId(null));
  };

  const handleBack = () => {
    if (viewMode !== "products") return;
    if (orderItems.length > 0) {
      setDiscardModalOpen(true);
      return;
    }
    performBack();
  };
  handleBackRef.current = handleBack;

  const handleInfo = () => {
    if (selectedItemId) {
      setModalOpen(true);
    }
  };

  // „Промени" (F9) редактира вече маркирания ред на избрания продукт.
  // Немаркиран продукт няма какво да промени: бутонът е заключен, а F9 не
  // прави нищо.
  const selectedItemMarked =
    selectedItemId != null &&
    orderItems.some((orderItem) => orderItem.itemId === selectedItemId);
  const handleModify = () => {
    if (selectedItemId != null) orderLineEdit.openFromCatalog(selectedItemId);
  };
  modifyRef.current = handleModify;

  // Само добавките с ненулево количество (модалът може да остави празни ключове)
  const activeAddonEntries = (addons: Record<number, Record<number, number>>) =>
    Object.entries(addons)
      .map(
        ([addonId, qtyMap]) =>
          [
            addonId,
            Object.entries(qtyMap)
              .filter(([, qty]) => qty > 0)
              .sort((a, b) => Number(a[0]) - Number(b[0])),
          ] as const,
      )
      .filter(([, entries]) => entries.length > 0)
      .sort((a, b) => Number(a[0]) - Number(b[0]));

  // Добавка с брояч (тип "1", "по избор") — количеството ѝ е АБСОЛЮТНО за реда,
  // затова редове с такава добавка НИКОГА не се сливат (сливането е двусмислено).
  const hasCounterAddon = (addons: Record<number, Record<number, number>>) =>
    activeAddonEntries(addons).some(([addonId]) => {
      const addon = allAddons.find((a) => a.id === Number(addonId));
      return String(addon?.type) === "1";
    });

  const sameModifiers = (a: number[], b: number[]) =>
    a.length === b.length &&
    [...a].sort((x, y) => x - y).join(",") ===
      [...b].sort((x, y) => x - y).join(",");

  const sameAddons = (
    a: Record<number, Record<number, number>>,
    b: Record<number, Record<number, number>>,
  ) =>
    JSON.stringify(activeAddonEntries(a)) ===
    JSON.stringify(activeAddonEntries(b));

  const handleModifyAdd = (data: OrderLineChoice) => {
    if (!selectedItemId) return;
    // Обеден продукт с дневен лимит: не повече от останалите порции
    if (lunchLimits.allowedQuantity(selectedItemId, data.quantity) < data.quantity) return;

    // Сливаме с ПОСЛЕДНИЯ ред само ако е АБСОЛЮТНО същият (продукт + модификатори
    // + добавки + коментар) — така А,А,А дава един ред ×3, но А,Б,А остава 3 реда
    // (редът на маркиране се пази). Редове с добавка с брояч (тип 1) не се сливат,
    // нито ред със свое време за кухнята: новото взима времето за всички.
    const last = orderItems[orderItems.length - 1];
    if (
      last &&
      last.kitchenHoldTime === undefined &&
      last.itemId === selectedItemId &&
      !hasCounterAddon(data.addons) &&
      !hasCounterAddon(last.addons) &&
      sameModifiers(last.modifiers, data.modifiers) &&
      sameAddons(last.addons, data.addons) &&
      (last.comment || "") === (data.comment || "")
    ) {
      const merged = [...orderItems];
      merged[merged.length - 1] = {
        ...last,
        quantity: last.quantity + data.quantity,
      };
      setOrderItems(merged);
    } else {
      const newOrderItem: OrderItem = {
        itemId: selectedItemId,
        ...data,
      };
      setOrderItems([...orderItems, newOrderItem]);
    }
    setModifyAddSignal((s) => s + 1);
  };

  // Двоен клик на ред от историята на масата — отваря „Промени" за същия
  // продукт като shortcut за повторна поръчка.
  const handleOrderRowReorder = async (productId: number, lunchMenuId?: number | null) => {
    // Закриването на масата още чака принтера — сметката не приема поръчки.
    if (isSelectedBillClosing) return;
    // Ред от обедно меню се поръчва пак САМО от обедното и само докато то е
    // активно — проверява се свежо (друг терминал може да го е приключил).
    // Свършило → не се допоръчва (нито на обедна, нито тихо на основна цена).
    if (lunchMenuId != null) {
      await checkLunchMenus(0);
      if (!isLunchItemOnSale(queryClient.getQueryData(["menu"]), lunchMenuId, productId)) {
        message.warning(t("lunchMenu.reorderEnded"));
        return;
      }
      if (lunchLimits.isSoldOutFresh(queryClient.getQueryData<MenuResponse>(["menu"]), lunchMenuId, productId))
        return;
      setSelectedItemId(lunchItemId(lunchMenuId, productId));
      setReorderMode(true);
      setModifyModalOpen(true);
      return;
    }
    const item = allItems.find((i) => i.id === productId);
    if (!item) {
      message.warning(t("ordering.reorderUnavailable"));
      return;
    }
    setSelectedItemId(item.id);
    setReorderMode(true);
    setModifyModalOpen(true);
  };
  orderRowReorderRef.current = handleOrderRowReorder;

  // Потвърждение от „Промени" в режим повторна поръчка — праща поръчката
  // директно към избраната сметка (без кошница и без смяна на изгледа).
  const handleReorderAdd = async (data: OrderLineChoice) => {
    if (!selectedItemId || !selectedBillId) return;
    if (createBillMutation.isPending) return;
    // Повторната поръчка отива направо в сметката — кошницата не се брои
    if (lunchLimits.allowedQuantity(selectedItemId, data.quantity, 0) < data.quantity) return;

    try {
      // В try: продукт, изчезнал от каталога (напр. свършило обедно меню),
      // хвърля — показваме грешка вместо тихо засядане
      const elements = buildBillElements([
        { itemId: selectedItemId, ...data },
      ]);
      await createBillMutation.mutateAsync({
        billId: selectedBillId,
        elements,
        comment: "",
      });
      message.success(t("ordering.productsAddedSuccess"));
      // Обедни порции → свежият брой остават за всички продукти
      if (elements.some((element) => element.lunchMenuId != null)) void checkLunchMenus(0);
    } catch (err: unknown) {
      if (await handleLunchEnded(err)) return;
      // При мрежова грешка `detail` от BE-то няма и общото „Грешка" не казва
      // нищо. Поръчката е МУТАЦИЯ и не се повтаря сама (виж `api/client.ts`
      // §Повторение) — затова текстът праща човека да ПОГЛЕДНЕ масата,
      // вместо да натисне пак наслука.
      message.error(
        getApiErrorMessage(
          err,
          isCloudUnreachableError(err)
            ? t("ordering.orderCloudUnreachable")
            : t("common.error"),
        ),
      );
    }
  };

  // Сървърът отказа обеден продукт (409 `lunch_menu_ended`): менюто е свършило
  // или друг терминал го е приключил/изтрил. Свежо меню → групата изчезва, а
  // кошницата минава на основната цена (useEndedLunchCartItems); сервитьорът
  // вижда какво е станало и поръчва наново.
  // 409 `lunch_limit_reached`: порциите свършиха (друг сервитьор взе
  // последните) — свеж брой и какво остава; поръчката не е записана.
  const handleLunchEnded = async (err: unknown) => {
    const limitMessage = lunchLimits.limitErrorMessage(err);
    if (limitMessage != null) {
      await checkLunchMenus(0);
      message.warning(limitMessage);
      return true;
    }
    if (getEndedLunchMenuIds(err) == null) return false;
    await checkLunchMenus(0);
    message.warning(t("lunchMenu.orderEnded"));
    return true;
  };

  // Строи payload елементите за CreateBillIn от редове на кошницата —
  // ползва се и от подаването на кошницата, и от бързата повторна поръчка.
  const buildBillElements = (items: OrderItem[]) =>
    items.map((orderItem) => {
      const item = allItems.find((i) => i.id === orderItem.itemId);
      if (!item) throw new Error(`Item ${orderItem.itemId} not found`);

      // stationIds е гарантирано непразен (виж Item) — ?? 1 е типова застраховка
      const stationId = item.stationIds?.[0] ?? 1;

      return {
        // Копие от обедно меню → истинският артикул/рецепта + обедното меню
        // (цената по-долу е обедната) — виж lib/lunchCatalog.ts
        productId: sourceItemIdOf(item),
        ...(item.lunchMenuId != null ? { lunchMenuId: item.lunchMenuId } : {}),
        stationId: stationId,
        quantity: orderItem.quantity.toString(),
        price: item.price.toString(),
        lockedPrice: item.lockedPrice,
        percent: "0",
        lineTotal: (item.price * orderItem.quantity).toString(),
        status: "0",
        modifiers: [
          ...orderItem.modifiers.map((modId) => ({
            modifier_id: modId.toString(),
          })),
          ...(orderItem.comment ? [{ modifier_id: orderItem.comment }] : []),
        ],
        addons: Object.entries(orderItem.addons).flatMap(([addonId, qtyMap]) =>
          Object.entries(qtyMap).map(([addonItemId, addonQty]) => {
            const itemId = Number(addonItemId);
            const addon = allAddons.find((a) => a.id === Number(addonId));
            const addonItemData = addon?.items.find(
              (ai) => ai.itemId === itemId,
            );
            const addonPrice =
              addonItemData?.addonItemPrice ?? addonItemData?.price ?? 0;
            const addonItem = allItems.find((i) => i.id === itemId);
            const addonStationId = addonItem?.stationIds?.[0] ?? 1;
            // Изпращаме ФИНАЛНОТО количество (бекендът не умножава):
            // тип "1" (обикновена) — броячът от модала е АБСОЛЮТЕН (не зависи
            // от бройката на продукта); тип "2"/"3" — по 1 на продукт (легаси).
            const absolute = String(addon?.type) === "1";
            const effectiveQty = absolute
              ? addonQty
              : addonQty * orderItem.quantity;
            return {
              addition_item_id: itemId,
              station_id: addonStationId,
              quantity: effectiveQty.toString(),
              price: addonPrice.toString(),
              percent: "0",
              total_sum: (addonPrice * effectiveQty).toString(),
            };
          }),
        ),
      };
    });

  const handleConfirmOrder = async (comment: string) => {
    // Колегата е избран в „Нова маса“, но междувременно е минало в локален
    // режим: касата пренебрегва `waiterId` и би отворила масата на влезлия.
    // Не се праща — кошницата остава, „Поръчай“ минава, щом се върне връзката.
    if (edgeLocal && !selectedBillId && newTableWaiterId != null) {
      message.error(t("ordering.newTableWaiter.offlineBlocked"));
      return;
    }
    try {
      // В try: продукт, изчезнал от каталога, хвърля — виж handleReorderAdd
      const billData: CreateBillIn = {
        billId: selectedBillId || 0,
        tableName: newTableName || undefined,
        elements: buildBillElements(orderItems),
        comment: comment || "",
        // Клиент, избран преди откриването (нова маса) — BE го закача веднага
        // след създаването на сметката
        discount:
          !selectedBillId && pendingDiscount ? pendingDiscount : undefined,
        // Брой гости от модала преди „Нова маса" — само за новата сметка
        guests:
          !selectedBillId && newTableGuests != null ? newTableGuests : undefined,
        // Сервитьорът, избран преди „Нова маса" — само за новата сметка
        waiterId:
          !selectedBillId && newTableWaiterId != null ? newTableWaiterId : undefined,
        // Времето за кухнята: всичко „Сега" — полето го няма и поръчката
        // влиза веднага; иначе — по група на време с индексите на редовете
        kitchenHold: kitchenHold.payload,
      };

      // Запазваме response-а за да вземем billId на новата/обновена сметка
      const result = await createBillMutation.mutateAsync(billData);
      // Обедни порции → свежият брой остават (виж handleReorderAdd)
      if (billData.elements.some((element) => element.lunchMenuId != null))
        void checkLunchMenus(0);
      // При лична номерация „Нова маса" може да влезе в празната сметка от
      // „Настани" (резервации) — тогава сметката вече беше в списъка.
      const joinedExistingBill =
        newTableName != null && bills.some((bill) => bill.id === result.id);
      message.success(
        !newTableName
          ? t("ordering.productsAddedSuccess")
          : joinedExistingBill
            ? t("ordering.tableJoinedSuccess", { name: newTableName })
            : t("ordering.tableCreatedSuccess", { name: newTableName }),
      );
      setOrderItems([]);
      setSelectedItemId(null);
      setSelectedCategoryId(null);
      setNewTableName(null);
      setNewTableWaiterId(null);
      setPendingDiscount(null);
      setOrderComment("");
      setShowCommentInput(false);
      kitchenHold.reset();
      setSheetOpen(false);
      // Задаваме selectedBillId на новата/обновена сметка за да остане фокусът върху нея
      dispatch(setSelectedBillId(result.id));
      setViewMode("tables");
      // Бързо закриване: веднага след поръчката отваряме модала „Закрий" за
      // същата сметка (TableFooter изчаква тя да се появи в bills списъка).
      // Задържана поръчка — не: закриването би я пуснало в кухнята веднага.
      if (quickCloseEnabled && !billData.kitchenHold) setPendingCloseBillId(result.id);
    } catch (err: unknown) {
      if (await handleLunchEnded(err)) return;
      // Проверка дали грешката е за дублирано име на маса
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || String(err);
      const isDuplicateTableError =
        errorMessage.toLowerCase().includes("table") &&
        (errorMessage.toLowerCase().includes("exist") ||
          errorMessage.toLowerCase().includes("duplicate") ||
          errorMessage.toLowerCase().includes("already"));

      if (isDuplicateTableError && newTableName) {
        // Показваме грешка за дублирана маса и отваряме модала отново
        message.error(t("ordering.tableAlreadyExists", { name: newTableName }));
        // Изчистваме името и отваряме модала отново
        setNewTableName(null);
        setNewTableWaiterId(null);
        setNewTableModalOpen(true);
        setViewMode("tables");
      } else {
        // Обща грешка — локализирана, не суровият detail. Мрежовата грешка си
        // има свой текст: тя не значи „отказано", а „не се знае".
        message.error(
          getApiErrorMessage(
            err,
            isCloudUnreachableError(err)
              ? t("ordering.orderCloudUnreachable")
              : t("common.error"),
          ),
        );
      }
    }
  };

  // Подава поръчката директно от панела/sheet-а (и от Enter). Guard-ове:
  // празна кошница → предупреждение; заявка в движение → игнорираме (пази от
  // двойно подаване, тъй като Enter вече стреля директно).
  const handleSubmitOrder = () => {
    if (orderItems.length === 0) {
      message.warning(t("ordering.selectProductsWarning"));
      return;
    }
    if (createBillMutation.isPending) return;
    void handleConfirmOrder(orderComment);
  };
  submitOrderRef.current = handleSubmitOrder;

  // Toggle на полето за коментар към поръчката (затварянето чисти текста —
  // същото поведение като стария summary footer).
  const handleToggleComment = () => {
    if (showCommentInput) {
      setShowCommentInput(false);
      setOrderComment("");
    } else {
      setShowCommentInput(true);
    }
  };

  const handleOrderItemQuantityChange = (index: number, delta: number) => {
    const newOrderItems = [...orderItems];
    const item = newOrderItems[index];

    let actualDelta = delta;
    // „+" на ред в кошницата: обеден продукт с дневен лимит — не повече от
    // останалите порции (както „+" в каталога)
    if (delta > 0 && lunchLimits.allowedQuantity(item.itemId, delta) < delta) return;

    // При намаляване, специална логика
    if (delta < 0) {
      if (item.quantity === 0.5) {
        // Ако е точно 0.5, махаме 0.5
        actualDelta = -0.5;
      }
      // Иначе винаги махаме 1 (дори да стане отрицателно)
    }

    const newQuantity = item.quantity + actualDelta;

    if (newQuantity <= 0) {
      // Премахваме елемента ако количеството стане 0 или по-малко
      newOrderItems.splice(index, 1);
    } else {
      // Актуализираме количеството
      item.quantity = newQuantity;
    }

    setOrderItems(newOrderItems);
  };

  const handleNewTable = async () => {
    // „Отказ" в „Брой гости" спира „Нова маса" изцяло
    if (!(await askBeforeNewTable())) return;
    if (!quickCloseEnabled) {
      setNewTableModalOpen(true);
      return;
    }
    await openNextTable();
  };

  const handleNewTableSubmit = (tableName: string, waiterId?: number) => {
    setNewTableName(tableName);
    setNewTableWaiterId(waiterId ?? null);
    dispatch(setSelectedBillId(null));
    // Чисти евентуална остатъчна селекция от повторна поръчка в изгледа с маси
    setSelectedItemId(null);
    setViewMode("products");
  };

  const handleAddToTable = () => {
    // Масата, чието закриване чака принтера, не приема поръчки (и F6, и бутонът
    // във футъра са заключени — това е последната преграда).
    if (selectedBillId && !isSelectedBillClosing) {
      setSelectedItemId(null);
      setViewMode("products");
    }
  };

  // Селекция (вкл. keyboard фокус-sync на десктоп) — НЕ отваря модал.
  const handleItemSelect = (itemId: number | null) => {
    setSelectedItemId(itemId);
  };

  const handleQuantityChange = (itemId: number, newTotalQuantity: number) => {
    // Намираме всички записи за този продукт
    const allItemRecords = orderItems.filter((item) => item.itemId === itemId);

    // Изчисляваме текущото общо количество
    const currentTotal = allItemRecords.reduce(
      (sum, item) => sum + item.quantity,
      0,
    );

    // Обеден продукт с дневен лимит: не повече от останалите порции
    let targetTotal = newTotalQuantity;
    if (targetTotal > currentTotal) {
      const allowed = lunchLimits.allowedQuantity(itemId, targetTotal - currentTotal, currentTotal);
      if (allowed <= 0) return;
      targetTotal = currentTotal + allowed;
    }

    // При инкремент проверяваме за задължителни (type === "2") / препоръчителни
    // (type === "3") добавки или задължителен модификатор (isRequired)
    if (targetTotal > currentTotal && itemsRequiringAddons.has(itemId)) {
      // Селектираме продукта и отваряме „Промени" за нов ред
      setSelectedItemId(itemId);
      setModifyModalOpen(true);
      return;
    }
    // Изчисляваме разликата
    const diff = targetTotal - currentTotal;

    // "Прост" запис = без модификатори, без добавки, без коментар (създаден от "+")
    const isSimpleRecord = (item: OrderItem) =>
      item.modifiers.length === 0 &&
      Object.keys(item.addons).length === 0 &&
      !item.comment;

    if (diff > 0) {
      // Инкремент — сливаме с ПОСЛЕДНИЯ ред, ако той е същият продукт без
      // добавки/модификатори/коментар (какъвто създава "+"): 3ד+" = един ред ×3.
      // Ако горният ред е друг продукт (или има добавки/модификатори) — нов ред,
      // за да се пази редът на маркиране (А,Б,А = 3 реда). Ред със свое време
      // за кухнята не поема новото — то взима времето за всички.
      const last = orderItems[orderItems.length - 1];
      if (
        last &&
        last.kitchenHoldTime === undefined &&
        last.itemId === itemId &&
        isSimpleRecord(last)
      ) {
        const merged = [...orderItems];
        merged[merged.length - 1] = {
          ...last,
          quantity: last.quantity + diff,
        };
        setOrderItems(merged);
      } else {
        const newOrderItem: OrderItem = {
          itemId,
          quantity: diff,
          modifiers: [],
          addons: {},
          comment: "",
        };
        setOrderItems([...orderItems, newOrderItem]);
      }
    } else if (diff < 0) {
      // Декремент — махаме от последно добавените записи назад:
      // първо от "простите" (създадени от "+"), после от тези с добавки/модификатори.
      const newOrderItems = [...orderItems];
      let leftToDecrement = Math.abs(diff);

      const drainFromEnd = (onlySimple: boolean) => {
        for (
          let i = newOrderItems.length - 1;
          i >= 0 && leftToDecrement > 0;
          i--
        ) {
          const record = newOrderItems[i];
          if (record.itemId !== itemId) continue;
          if (onlySimple && !isSimpleRecord(record)) continue;

          if (record.quantity > leftToDecrement) {
            record.quantity -= leftToDecrement;
            leftToDecrement = 0;
          } else {
            leftToDecrement -= record.quantity;
            newOrderItems.splice(i, 1);
          }
        }
      };

      drainFromEnd(true);
      drainFromEnd(false);

      setOrderItems(newOrderItems);
    }
  };

  return (
    <>
      <TablesAutoSelect
        bills={bills}
        sortedBills={sortedBills}
        selectedBillId={selectedBillId}
        isTablesView={viewMode === "tables"}
        autoSelectFirst={!schemaMode}
      />
      <OrderingShell>
        {/* Горната лента е при масите и (под десктоп прага) при продуктите —
            там стрелката ѝ е единственият „назад". На десктоп продуктовият
            изглед е без лента: Назад/профил/Backoffice са във футъра. */}
        {(viewMode === "tables" || !isDesktop) && (
          <OrderingTopBar
            onBack={viewMode === "products" ? handleBack : undefined}
            viewSwitch={
              viewMode === "tables" && canSwitch ? (
                <TablesViewSwitch
                  value={tablesView}
                  onChange={setTablesView}
                  // В режим „Схема" микрофонът е тук — списъкът с търсачката го няма.
                  onVoiceOrder={
                    schemaMode && canVoiceOrder
                      ? () => setVoiceModalOpen(true)
                      : undefined
                  }
                  voiceLocked={edgeLocal}
                />
              ) : undefined
            }
          />
        )}

        {viewMode === "tables" ? (
          <TablesScreen
            schema={
              schemaMode
                ? {
                    floor: schema.floor,
                    selection: schema.selection,
                    onOpenBill: schema.canOpenBill ? openBillInMenu : undefined,
                  }
                : null
            }
            selectedBill={selectedBill}
            closingBillIds={closingBillIds}
            listLoading={billsLoading && tableBills.length === 0}
            list={{
              bills: tableBills,
              closingBillIds,
              selectedBillId,
              onTableSelect: handleTableSelect,
              onTableKeyboardSelect: (billId) =>
                dispatch(setSelectedBillId(billId)),
              onVoiceOrder: canVoiceOrder
                ? () => setVoiceModalOpen(true)
                : undefined,
              voiceLocked: edgeLocal,
              onTableDoubleClick: isNarrow
                ? (billId) => {
                    handleTableSelect(billId);
                    setRailHidden(true);
                  }
                : undefined,
              showClosedTables,
              isModalOpen: tablesModalOpen,
              itemsKeyboardActive: billItemsKeyboard.active,
              onArrowRightEdge: billItemsKeyboard.onArrowRightEdge,
            }}
            railHidden={railHidden && isNarrow}
            onShowRail={
              railHidden && isNarrow ? () => setRailHidden(false) : undefined
            }
            details={{
              selectedItemId: selectedOrderItemId,
              onItemSelect: setSelectedOrderItemId,
              onItemReorder: handleOrderRowReorder,
              summarized: billSummarized,
              onToggleSummarized: toggleBillSummarized,
            }}
            footer={{
              onNewTable: schema.openFreeTable ?? handleNewTable,
              onAddToTable: handleAddToTable,
              // Схема: винаги през маса — свободна избрана маса → „Нова маса";
              // сметка, която приема поръчки → „Добави"; нищо избрано → нито един.
              newTableDisabled: schemaMode && schema.openFreeTable === null,
              // Схема: часовникът е вляво в лентата (релсата с маси я няма).
              leading: schemaMode ? <TableFooterClock /> : undefined,
              selectedTableId: selectedBillId,
              selectedTableName: selectedBill?.name,
              onClearSelection: () => setSelectedOrderItemId(null),
              onRenameModalOpenChange: setRenameTableModalOpen,
              onAnyModalOpenChange: setFooterModalOpen,
              autoCloseBillId: pendingCloseBillId,
              onAutoCloseHandled: () => setPendingCloseBillId(null),
              onReservationSeated: handleReservationSeated,
            }}
          />
        ) : (
          <>
            <div
              className={`flex flex-1 overflow-hidden min-h-0 ${
                isDesktop
                  ? "pb-[66px]"
                  : "pb-[calc(52px+env(safe-area-inset-bottom,0px)*0.5)]"
              }`}
            >
              {/* Основна колона: категорийни chips + търсачка + продуктов списък */}
              <main className="flex-1 flex flex-col overflow-hidden bg-pos-canvas min-w-0">
                {/* На телефон отделният хедър (Назад/„Добави"/маса) е премахнат
                    (0.0.117) — назад е стрелката в горната лента, а името на
                    масата стои до категорийния селектор (66/33) */}
                {/* Над каталога: групите (дърво) или станциите (плосък ред)
                    според настройката на ресторанта catalogFilter — на десктоп;
                    под прага винаги свитият селектор на групите */}
                {isDesktop && catalogFilter === "stations" ? (
                  <StationChips
                    selectedStationId={selectedStationId}
                    onStationSelect={setSelectedStationId}
                  />
                ) : (
                  <CategoryChips
                    selectedCategoryId={selectedCategoryId}
                    onCategorySelect={setSelectedCategoryId}
                    collapsed={!isDesktop}
                    trailing={
                      !isDesktop && tableLabel ? (
                        // Името на масата — статична карта като селектора:
                        // акцентна лента вляво, kicker „МАСА" + името отдолу
                        <div className="h-10 min-w-0 flex items-center gap-2.5 rounded-xl bg-pos-card overflow-hidden">
                          <span
                            aria-hidden
                            className="w-1.5 self-stretch bg-pos-accent shrink-0"
                          />
                          <div className="min-w-0 flex flex-col justify-center gap-0.5 pr-3 leading-none">
                            <span className="font-pos-mono text-[9px] font-semibold tracking-[.14em] uppercase text-pos-sec-head">
                              {t("ordering.tableKicker")}
                            </span>
                            <span className="text-[15px] font-extrabold text-pos-ink truncate">
                              {tableLabel}
                            </span>
                          </div>
                        </div>
                      ) : undefined
                    }
                  />
                )}

                <div className="flex-1 min-h-0 overflow-hidden">
                  <ItemCatalog
                    categoryId={selectedCategoryId}
                    selectedItemId={selectedItemId}
                    onItemSelect={handleItemSelect}
                    isModalOpen={
                      modalOpen ||
                      modifyModalOpen ||
                      discardModalOpen ||
                      sheetOpen ||
                      clientsModalOpen ||
                      editModifiersOpen
                    }
                    orderItems={orderItems}
                    onQuantityChange={handleQuantityChange}
                    onBack={handleBack}
                    itemsRequiringAddons={itemsRequiringAddons}
                    addonModalAddSignal={modifyAddSignal}
                    view={catalogView}
                    sort={configuration.catalogSort}
                    billDiscountPercent={billDiscount?.percent ?? 0}
                    stationId={isDesktop ? selectedStationId : null}
                    colorBy={configuration.colorBy}
                    searchFieldVisible={configuration.orderingSearchField}
                    searchScope={configuration.orderingSearchScope}
                    onExitCategory={() => setSelectedCategoryId(null)}
                    keyboardOpen={keyboardOpen}
                    keyboardLayout={configuration.keyboardLayout}
                    onKeyboardClose={() => setKeyboardOpen(false)}
                  />
                </div>
              </main>

              {/* Постоянен панел „Преглед на поръчката" — само на десктоп */}
              {isDesktop && (
                <OrderPanel
                  orderItems={orderItems}
                  comment={orderComment}
                  showCommentInput={showCommentInput}
                  onCommentChange={setOrderComment}
                  onToggleComment={handleToggleComment}
                  onQuantityChange={handleOrderItemQuantityChange}
                  onItemEdit={orderLineEdit.openFromPanel}
                  onSubmit={handleSubmitOrder}
                  submitting={createBillMutation.isPending}
                  billDiscount={billDiscount}
                  previewFilterKind={previewFilterKind}
                  previewFilter={previewFilter}
                  onPreviewFilterChange={handlePreviewFilterChange}
                  colorBy={configuration.colorBy}
                  kitchenHold={kitchenHold.controls}
                />
              )}
            </div>

            {/* Долен стек: футърът с бутони е САМО на десктоп (на телефон
                Назад = стрелката в хедъра, Инфо е в „Промени", а „Промени"
                се отваря с клик на продукт) + мобилната лента на sheet-а */}
            <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col">
              {isDesktop && (
                <OrderFooter
                  onBack={handleBack}
                  onInfo={handleInfo}
                  onModify={handleModify}
                  hasSelectedItem={!!selectedItemId}
                  canModify={selectedItemMarked}
                  onClients={
                    canSelectCustomer
                      ? () => setClientsModalOpen(true)
                      : undefined
                  }
                  clientsDisabled={!selectedBillId && !newTableName}
                  catalogView={catalogView}
                  onCatalogViewChange={handleCatalogViewChange}
                  keyboardOpen={keyboardOpen}
                  onToggleKeyboard={() => setKeyboardOpen((open) => !open)}
                  tableName={tableLabel}
                />
              )}
              {!isDesktop && (
                <MobileOrderBar
                  orderItems={orderItems}
                  onClick={() => setSheetOpen(true)}
                  billDiscountPercent={billDiscount?.percent ?? 0}
                />
              )}
            </div>

            {/* Мобилен bottom sheet „Преглед на поръчката" */}
            <OrderSheet
              open={sheetOpen && !isDesktop}
              onClose={() => setSheetOpen(false)}
              orderItems={orderItems}
              comment={orderComment}
              showCommentInput={showCommentInput}
              onCommentChange={setOrderComment}
              onToggleComment={handleToggleComment}
              onQuantityChange={handleOrderItemQuantityChange}
              onItemEdit={orderLineEdit.openFromPanel}
              onSubmit={handleSubmitOrder}
              submitting={createBillMutation.isPending}
              billDiscount={billDiscount}
              kitchenHold={kitchenHold.controls}
            />

            {/* „Клиенти" от футъра на продуктовия изглед — преизползва модала
                на екрана с масите; отстъпката се прилага веднага по сметката,
                а каталогът/панелът показват коригираните цени */}
            <ClientsModal
              open={clientsModalOpen}
              onClose={() => setClientsModalOpen(false)}
              billId={selectedBillId}
              pendingDiscount={pendingDiscount}
              onPendingSelect={setPendingDiscount}
            />
          </>
        )}

        {/* Modal за детайли на избран артикул */}
        {selectedItem && (
          <ItemDetailModal
            item={selectedItem}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}

        {/* „Промени" за маркиран ред — моливът в „Преглед на поръчката" и
            бутонът „Промени" (F9) */}
        <EditModifiersModal
          item={
            editModifiersTarget
              ? (allItems.find(
                  (item) => item.id === editModifiersTarget.itemId,
                ) ?? null)
              : null
          }
          open={editModifiersOpen}
          initial={editModifiersTarget}
          focusField={editModifiersTarget?.focusField}
          onClose={orderLineEdit.close}
          onSave={orderLineEdit.save}
        />

        {/* „Промени" за нов ред — „+" на продукт със задължителни/
            препоръчителни добавки или задължителен модификатор и повторната
            поръчка от историята на масата */}
        {selectedItem && (
          <EditModifiersModal
            item={selectedItem}
            open={modifyModalOpen}
            isNewLine
            onClose={() => {
              setModifyModalOpen(false);
              setReorderMode(false);
            }}
            onSave={reorderMode ? handleReorderAdd : handleModifyAdd}
            confirmLabelKey={reorderMode ? "ordering.modifyOrder" : "ordering.modifyDone"}
            onInfo={!isDesktop ? handleInfo : undefined}
          />
        )}

        {/* Modal за потвърждаване на излизане при маркирани продукти */}
        <DiscardOrderModal
          open={discardModalOpen}
          onCancel={() => setDiscardModalOpen(false)}
          onConfirm={() => {
            setDiscardModalOpen(false);
            performBack();
          }}
        />

        {/* Modal за гласова поръчка (говориш → системата маркира) */}
        <VoiceOrderModal
          open={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
          personalNumberingEnabled={personalNumberingEnabled}
          currentUserId={currentUserId}
          onSeatReservation={voiceSeat.onSeatReservation}
        />
        {voiceSeat.seatModal}

        {/* Нова маса: с правото за чужди сметки — първо сервитьорът, после номерът */}
        <NewTableFlow
          open={newTableModalOpen}
          onClose={() => setNewTableModalOpen(false)}
          onSubmit={handleNewTableSubmit}
          suggestName={configuration.orderingSuggestTableName}
        />
        {guestCountModal}

        {reservationConfirmModal}
      </OrderingShell>
    </>
  );
};

export default Ordering;
